import EmployeeSectionWrapper from '../shared/EmployeeSectionWrapper';
import { READ_EMPLOYEE_SKILLS } from '../skills/useEmployeeSkillsService';
import {
  isPolishContractorByEmployeeType,
  READ_CAREER_DEVELOPMENT_NEXT_REVIEW_DATE,
  READ_EMPLOYEE_CAREER_DEVELOPMENT_INFORMATION,
  READ_EMPLOYEE_MAIN_HR_INFORMATION,
  READ_EMPLOYEE_PEOPLE_MANAGEMENT_RULES,
  READ_EMPLOYEE_SPECIALIZATION_INFO,
} from 'domain/shared/employee';
import CompanyRolesList from './companyRoles/lists/CompanyRolesList';
import { READ_EMPLOYEE_COMPANY_ROLES } from './companyRoles/shared/companyRolesService';
import SpecializationInfoSection from './specializationSection/SpecializationInfoSection';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import CareerDevelopmentReviewSection from './qualification/Show/CareerDevelopmentReviewSection';
import MenagmenetSection from './managementSection/MenagmenetSection';
import { styled, Box, Divider } from '@mui/material';
import { Theme } from '@mui/material/styles';
import MentorshipSection from './mentorship/MentorshipSection';
import { useAuthorization } from 'framework/authorization';
import { READ_ASSESSMENTS_LIST } from '../assessment/assessmentService';
import AssessmentSection from './assessmentSection/AssessmentSection';
import EmployeeSkills from '../skills/EmployeeSkills';
import ExpertiseSection from '../expertise/ExpertiseSection';
import { ExpertisePermission } from '../expertise/service/expertiseService.model';
import { useShowContext } from 'framework/show';

const EmployeeGrid = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 0.5fr) minmax(0, 0.5fr)',
  columnGap: '16px',
  alignItems: 'start',
  width: '100%',
  position: 'relative',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
})) as typeof Box;

const CareerCapabilitiesPage = () => {
  const { isFeatureEnabled } = useFeatureToggle();
  const { isGranted } = useAuthorization();
  const { data } = useShowContext();
  const currentEmployee = {
    employeeType: data?.getValueBySource?.('employeeType'),
    officeId: data?.getValueBySource?.('officeId'),
    businessRegistrationCountry: data?.getValueBySource?.('businessRegistrationCountry'),
  };
  const isPolishContractor = isPolishContractorByEmployeeType(currentEmployee);
  const canHaveMenteesAsPolishContractor = !!data?.getValueBySource?.(
    'mentorshipInformation.canHaveMenteesAsPolishContractor',
  );

  const isPeopleManagementVisible = isGranted(READ_EMPLOYEE_PEOPLE_MANAGEMENT_RULES);
  const isCareerVisible = isGranted([
    READ_CAREER_DEVELOPMENT_NEXT_REVIEW_DATE,
    READ_EMPLOYEE_CAREER_DEVELOPMENT_INFORMATION,
  ]);
  const isSkillsVisible = isGranted(READ_EMPLOYEE_SKILLS);
  const isAssessmentsVisible = isGranted(READ_ASSESSMENTS_LIST);

  return (
    <>
      <EmployeeGrid>
        <EmployeeSectionWrapper
          permissions={[READ_EMPLOYEE_SPECIALIZATION_INFO, READ_EMPLOYEE_MAIN_HR_INFORMATION]}
          withMinHeight={false}
          fullHeight
        >
          <SpecializationInfoSection />
        </EmployeeSectionWrapper>
        {isPeopleManagementVisible || isCareerVisible ? (
          <EmployeeGrid
            sx={{
              gridTemplateColumns:
                isPeopleManagementVisible && isCareerVisible
                  ? 'minmax(0, 0.5fr) minmax(0, 0.5fr)'
                  : 'minmax(0, 1fr) !important',
            }}
            height="100%"
          >
            <EmployeeSectionWrapper
              permissions={READ_EMPLOYEE_PEOPLE_MANAGEMENT_RULES}
              withMinHeight={false}
              fullHeight
            >
              <MenagmenetSection />
            </EmployeeSectionWrapper>

            <EmployeeSectionWrapper
              permissions={[READ_CAREER_DEVELOPMENT_NEXT_REVIEW_DATE, READ_EMPLOYEE_CAREER_DEVELOPMENT_INFORMATION]}
              withMinHeight={false}
              fullHeight
            >
              <CareerDevelopmentReviewSection />
            </EmployeeSectionWrapper>
          </EmployeeGrid>
        ) : null}
      </EmployeeGrid>

      {(!isPolishContractor || canHaveMenteesAsPolishContractor) && (
        <EmployeeSectionWrapper withMinHeight={false}>
          <MentorshipSection canHaveMenteesAsPolishContractor={canHaveMenteesAsPolishContractor} />
        </EmployeeSectionWrapper>
      )}

      <EmployeeGrid
        sx={{
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
        }}
      >
        <EmployeeSectionWrapper permissions={[READ_EMPLOYEE_SKILLS, READ_ASSESSMENTS_LIST]}>
          <Box>
            {isSkillsVisible && <EmployeeSkills />}
            {isSkillsVisible && isAssessmentsVisible && <Divider sx={{ my: 2 }} />}
            {isAssessmentsVisible && <AssessmentSection />}
          </Box>
        </EmployeeSectionWrapper>
        {isFeatureEnabled(Features.EXPERTISE) && (
          <EmployeeSectionWrapper
            withMinHeight={false}
            fullHeight
            permissions={[ExpertisePermission.PEOPLE_MANAGEMENT_EMPLOYEE_EXPERTISE_GET]}
          >
            <ExpertiseSection />
          </EmployeeSectionWrapper>
        )}
      </EmployeeGrid>

      <EmployeeGrid>
        <EmployeeSectionWrapper permissions={READ_EMPLOYEE_COMPANY_ROLES}>
          <CompanyRolesList />
        </EmployeeSectionWrapper>
      </EmployeeGrid>
    </>
  );
};

export default CareerCapabilitiesPage;
